import { useEffect, useState } from 'react';
import {
  BUSINESS_GROUP_OPTIONS,
  CUSTOMER_PRICE_GROUP_OPTIONS,
  SALESPERSON_OPTIONS,
  type FinanceSelectOption,
} from './accountCreationFinanceOptions';
import { PAYMENT_TERMS_OPTIONS } from './accountCreationUi';
import {
  fetchFormFieldOptions,
  fetchSalespersonEmails,
  type FormFieldOption,
} from './applicationFormSettings';
import type { ApplyForOption, BillingSoaOption, PaymentTermsOption } from '../types/applicationForm';

export type FieldOptionsMap = Record<string, FinanceSelectOption[]>;

const FALLBACK: FieldOptionsMap = {
  apply_for: [
    { value: 'new_customer', label: 'New Account' },
    { value: 'new_outlet', label: 'New Outlet' },
  ],
  billing_soa: [
    { value: 'individual', label: 'Individual' },
    { value: 'group', label: 'Group' },
  ],
  payment_terms: PAYMENT_TERMS_OPTIONS.map((o) => ({ value: o.value, label: o.label })),
  businessgroup_code: BUSINESS_GROUP_OPTIONS,
  salesperson_code: SALESPERSON_OPTIONS,
  customer_price_group: CUSTOMER_PRICE_GROUP_OPTIONS,
};

function rowsToOptions(rows: FormFieldOption[]): FinanceSelectOption[] {
  return rows.map((row) => ({ value: row.value, label: row.label }));
}

export function useAccountCreationFieldOptions(): {
  options: FieldOptionsMap;
  loading: boolean;
  error: string | null;
} {
  const [options, setOptions] = useState<FieldOptionsMap>(FALLBACK);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      setError(null);
      try {
        const [rows, salespersonRows] = await Promise.all([
          fetchFormFieldOptions('account-creation'),
          fetchSalespersonEmails().catch(() => []),
        ]);
        if (cancelled) return;
        const grouped: FieldOptionsMap = { ...FALLBACK };
        for (const key of Object.keys(FALLBACK)) {
          if (key === 'salesperson_code') continue;
          const matches = rows.filter((row) => row.field_key === key);
          if (matches.length > 0) grouped[key] = rowsToOptions(matches);
        }
        const activeSales = salespersonRows.filter((row) => row.active);
        if (activeSales.length > 0) {
          grouped.salesperson_code = activeSales.map((row) => ({
            value: row.code,
            label: row.code,
          }));
        } else {
          const matches = rows.filter((row) => row.field_key === 'salesperson_code');
          if (matches.length > 0) grouped.salesperson_code = rowsToOptions(matches);
        }
        setOptions(grouped);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Failed to load form options.');
          setOptions(FALLBACK);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return { options, loading, error };
}

export function formatOptionLabel(value: string, optionList: FinanceSelectOption[]): string {
  const trimmed = value.trim();
  if (!trimmed) return '—';
  const match = optionList.find((opt) => opt.value === trimmed);
  return match?.label ?? trimmed;
}

export function isApplyForOption(value: string): value is ApplyForOption {
  return value === 'new_customer' || value === 'new_outlet';
}

export function isBillingSoaOption(value: string): value is BillingSoaOption {
  return value === 'individual' || value === 'group';
}

export function isPaymentTermsFromOptions(value: string, options: FinanceSelectOption[]): value is PaymentTermsOption {
  return options.some((opt) => opt.value === value);
}
